import { Box, useTheme } from '@mui/material';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highmaps';
import immutableUpdate from 'immutability-helper';
import { concat, find, findIndex, get, update } from 'lodash';
import React, { useEffect } from 'react';
import {
  getWorldMapBillOfLandingOption,
  getWorldMapCTDPortsOption,
  getWorldMapCustomerOption,
  getWorldMapVesselDetailOption,
  IWorldMapProps,
  WorldMapOptions,
} from 'src/components/HighChart/WorldMapChart/config';

interface IProps {
  isLoading?: boolean;
  height: number;
  type: WorldMapOptions;
  data: Array<number | [string, number | null] | null | Highcharts.SeriesMapDataOptions>;
  dataPoint?: Array<number | [number, number | null] | null | Highcharts.SeriesMappointDataOptions>;
  dataLine?: Array<number | [number, number | null] | null | Highcharts.SeriesMaplineDataOptions>;
  tooltipFormatter?: Highcharts.TooltipFormatterCallbackFunction;
}

const WorldMapChart = ({ height, type, data, dataPoint = [], dataLine = [], tooltipFormatter, isLoading }: IProps) => {
  const [mapData, setMapData] = React.useState(null);
  const theme = useTheme();

  useEffect(() => {
    fetch('https://code.highcharts.com/mapdata/custom/world.topo.json')
      .then((response) => response.json())
      .then((data) => {
        // Modify map data to combine Morocco and Western Sahara
        const westernSaharaFeature = find(get(data, 'objects.default.geometries'), (geometry) => geometry?.id === 'EH');
        let moroccoFeature = find(get(data, 'objects.default.geometries'), (geometry) => geometry?.id === 'MA');
        const moroccoFeatureIndex = findIndex(
          get(data, 'objects.default.geometries'),
          (geometry: any) => geometry?.id === 'MA',
        );
        const westernSaharaFeatureIndex = findIndex(
          get(data, 'objects.default.geometries'),
          (geometry: any) => geometry?.id === 'EH',
        );

        const mergeArcs = concat(westernSaharaFeature?.arcs, moroccoFeature?.arcs);

        moroccoFeature['arcs'] = mergeArcs;
        const modifiedDataAddNewMorocco = update(
          data,
          `objects.default.geometries[${moroccoFeatureIndex}]`,
          moroccoFeature,
        );

        const modifiedDataRemoveWesternSahara = immutableUpdate(modifiedDataAddNewMorocco, {
          objects: {
            default: {
              geometries: {
                $splice: [[westernSaharaFeatureIndex, 1]],
              },
            },
          },
        });

        setMapData(modifiedDataRemoveWesternSahara);
      })
      // .then((data) => setMapData(data))
      .catch(() => setMapData(null));
  }, []);

  if (!mapData || isLoading) {
    return (
      <Box height={height} display={'grid'} sx={{ placeItems: 'center' }}>
        Loading map data...
      </Box>
    );
  }

  const getOption: () => Highcharts.Options = () => {
    const optionProps = {
      mapData: mapData,
      height: height,
      theme: theme,
      data: data,
      dataPoint: dataPoint,
      dataLine: dataLine || [],
      // dataLine: [
      //   {
      //     geometry: {
      //       type: 'LineString',
      //       coordinates: [
      //         [121.59, 25.1],
      //         [-118.24, 34.05],
      //       ],
      //     },
      //   },
      // ],
      tooltipFormatter: tooltipFormatter,
    } as IWorldMapProps;
    switch (type) {
      case WorldMapOptions.company_bill_of_landing:
        return getWorldMapBillOfLandingOption({ ...optionProps });
      case WorldMapOptions.customer:
        return getWorldMapCustomerOption({ ...optionProps });
      case WorldMapOptions.ctd_ports:
        return getWorldMapCTDPortsOption({ ...optionProps });
      case WorldMapOptions.vessel_detail:
        return getWorldMapVesselDetailOption({ ...optionProps });
      default:
        return {};
    }
  };

  return <HighchartsReact highcharts={Highcharts} constructorType={'mapChart'} options={getOption()} />;
};

export default WorldMapChart;
